import React, { useState, useEffect } from 'react';
import { ethers } from 'ethers';
import WalletConnector, { WALLET_TYPES } from './WalletConnector';
import { ArrowDown, RefreshCw, Settings, ChevronDown, Zap, Activity, AlertTriangle } from 'lucide-react';
import tokenABI from '../contracts/tokenABI.json';
import swapABI from '../contracts/swapABI.json';
import usdtImg from '../images/usdt.png';
import bnbImg from '../images/opbnb.png';
import grichImg from '../images/logo.png';

// Contract addresses - replace with your actual addresses
const SWAP_CONTRACT_ADDRESS = "0xd0d7ca20E1CD1a167CCb99610B191744DC44216C"; 
const TOKEN_ADDRESS = "0x3edAdD42044d4fBC8b4a02414Ae4646608A3f309";
const USDT_ADDRESS = "0x9e5aac1ba1a2e6aed6b32689dfcf62a509ca96f3";


const GrichSwap = () => {
  // State variables
  const [account, setAccount] = useState(null);
  const [provider, setProvider] = useState(null);
  const [swapContract, setSwapContract] = useState(null);
  const [tokenContract, setTokenContract] = useState(null);
  const [usdtContract, setUsdtContract] = useState(null);
  const [walletType, setWalletType] = useState(null);
  
  const [tokenBalance, setTokenBalance] = useState('0');
  const [usdtBalance, setUsdtBalance] = useState('0');
  const [bnbBalance, setBnbBalance] = useState('0');
  
  const [fromToken, setFromToken] = useState('BNB');
  const [toToken, setToToken] = useState('GRICH');
  const [fromAmount, setFromAmount] = useState('');
  const [toAmount, setToAmount] = useState('');
  const [slippage, setSlippage] = useState(1); // 1% slippage by default
  
  const [grichPrice, setGrichPrice] = useState(0.011); // Default price in USD
  const [bnbPrice, setBnbPrice] = useState(500.00); // Default BNB price in USD
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isSwapping, setIsSwapping] = useState(false);
  const [error, setError] = useState('');
  const [txHash, setTxHash] = useState('');
  const [activeTab, setActiveTab] = useState('USDT');
  const [bnbSwapsEnabled, setBnbSwapsEnabled] = useState(true);

  // Connect wallet callback
  const handleConnect = async (address, web3Provider, type) => {
    setAccount(address);
    setProvider(web3Provider);
    setWalletType(type);
    
    // Initialize contracts
    try {
      const signer = web3Provider.getSigner();
      
      // Initialize contracts with signer
      const swapContractInstance = new ethers.Contract(
        SWAP_CONTRACT_ADDRESS,
        swapABI,
        signer
      );
      
      const tokenContractInstance = new ethers.Contract(
        TOKEN_ADDRESS,
        tokenABI,
        signer
      );
      
      const usdtContractInstance = new ethers.Contract(
        USDT_ADDRESS,
        tokenABI, // Using same ABI for simplicity, adjust if needed
        signer
      );
      
      setSwapContract(swapContractInstance);
      setTokenContract(tokenContractInstance);
      setUsdtContract(usdtContractInstance);
      
      // Load balances
      await loadBalances(address, tokenContractInstance, usdtContractInstance, web3Provider);
      
      // Get GRICH price from contract
      const price = await swapContractInstance.price();
      setGrichPrice(parseFloat(ethers.utils.formatUnits(price, 18)));
      
      // Get BNB price - you would typically get this from an oracle
      // but for this demo we'll use a fixed value or get it from your contract
      const lastBnbPrice = await swapContractInstance.lastBnbPrice();
      if (lastBnbPrice.gt(0)) {
        setBnbPrice(parseFloat(ethers.utils.formatUnits(lastBnbPrice, 18)));
      }

      // Check if BNB swaps are enabled
      const bnbSwapsStatus = await swapContractInstance.bnbSwapsEnabled();
      setBnbSwapsEnabled(bnbSwapsStatus);
      
      // If BNB swaps are disabled and activeTab is BNB, switch to USDT
      if (!bnbSwapsStatus && activeTab === 'BNB') {
        handleTabSwitch('USDT');
      }
    } catch (error) {
      console.error("Error initializing contracts:", error);
      setError("Failed to initialize contracts. Please try again.");
    }
  };
  
  // Disconnect wallet callback
  const handleDisconnect = () => {
    setAccount(null);
    setProvider(null);
    setWalletType(null);
    setTokenBalance('0');
    setUsdtBalance('0');
    setBnbBalance('0');
    setSwapContract(null);
    setTokenContract(null);
    setUsdtContract(null);
  };
  
  // Handle error callback
  const handleError = (errorMessage) => {
    setError(errorMessage);
    setTimeout(() => setError(''), 5000); // Clear error after 5 seconds
  };
  
  // Load user balances
  const loadBalances = async (address, tokenContract, usdtContract, provider) => {
    try {
      // Get token balance
      const tokenBal = await tokenContract.balanceOf(address);
      setTokenBalance(ethers.utils.formatUnits(tokenBal, await tokenContract.decimals()));
      
      // Get USDT balance
      const usdtBal = await usdtContract.balanceOf(address);
      setUsdtBalance(ethers.utils.formatUnits(usdtBal, await usdtContract.decimals()));
      
      // Get BNB balance
      const bnbBal = await provider.getBalance(address);
      setBnbBalance(ethers.utils.formatEther(bnbBal));
    } catch (error) {
      console.error("Error loading balances:", error);
      setError("Failed to load balances. Please try again.");
    }
  };
  
  // Refresh balances
  const refreshBalances = async () => {
    if (account && provider && tokenContract && usdtContract) {
      await loadBalances(account, tokenContract, usdtContract, provider);
    }
  };
  
  // Handle from token amount change
  const handleFromAmountChange = (value) => {
    setFromAmount(value);
    calculateToAmount(value);
  };
  
  // Calculate output amount based on input
  const calculateToAmount = (value) => {
    if (!value || isNaN(value) || parseFloat(value) <= 0) {
      setToAmount('');
      return;
    }
    
    let calculatedAmount = 0;
    
    try {
      if (fromToken === 'BNB' && toToken === 'GRICH') {
        // BNB to GRICH: BNB amount * BNB price / GRICH price
        calculatedAmount = (parseFloat(value) * bnbPrice) / grichPrice;
      } else if (fromToken === 'USDT' && toToken === 'GRICH') {
        // USDT to GRICH: USDT amount / GRICH price
        calculatedAmount = parseFloat(value) / grichPrice;
      } else if (fromToken === 'GRICH' && toToken === 'BNB') {
        // GRICH to BNB: GRICH amount * GRICH price / BNB price
        calculatedAmount = (parseFloat(value) * grichPrice) / bnbPrice;
      } else if (fromToken === 'GRICH' && toToken === 'USDT') {
        // GRICH to USDT: GRICH amount * GRICH price
        calculatedAmount = parseFloat(value) * grichPrice;
      }
      
      // Apply slippage reduction (for display purposes)
      const withSlippage = calculatedAmount * (1 - slippage / 100);
      
      setToAmount(withSlippage.toFixed(6));
    } catch (error) {
      console.error("Error calculating amount:", error);
      setToAmount('');
    }
  };
  
  // Swap tokens
  const handleSwap = async () => {
    if (!account || !swapContract || !fromAmount || parseFloat(fromAmount) <= 0) {
      setError("Please enter a valid amount and connect your wallet.");
      return;
    }
    
    setIsSwapping(true);
    setError('');
    setTxHash('');
    
    try {
      let tx;
      
      // Convert amount to proper units
      const decimals = fromToken === 'GRICH' 
        ? await tokenContract.decimals() 
        : fromToken === 'USDT' 
          ? await usdtContract.decimals() 
          : 18; // BNB has 18 decimals
      
      const amountInWei = ethers.utils.parseUnits(fromAmount, decimals);
      
      // Check if we need approval (only for ERC20 tokens)
      if (fromToken !== 'BNB') {
        const contractToApprove = fromToken === 'GRICH' ? tokenContract : usdtContract;
        const allowance = await contractToApprove.allowance(account, SWAP_CONTRACT_ADDRESS);
        
        if (allowance.lt(amountInWei)) {
          // Request approval
          const approveTx = await contractToApprove.approve(
            SWAP_CONTRACT_ADDRESS,
            ethers.constants.MaxUint256 // Approve max amount
          );
          
          await approveTx.wait();
        }
      }
      
      // Execute swap based on token pair
      if (fromToken === 'BNB' && toToken === 'GRICH') {
        // Check if BNB swaps are enabled
        const bnbSwapsStatus = await swapContract.bnbSwapsEnabled();
        if (!bnbSwapsStatus) {
          throw new Error("BNB swaps are currently disabled");
        }
        
        // Convert BNB price to wei format (1e18 precision)
        const bnbPriceInWei = ethers.utils.parseUnits(bnbPrice.toString(), 18);
        
        tx = await swapContract.swapBNBToToken(
          bnbPriceInWei,
          { value: amountInWei }
        );
      } else if (fromToken === 'USDT' && toToken === 'GRICH') {
        tx = await swapContract.swapUSDTToToken(amountInWei);
      } else if (fromToken === 'GRICH' && toToken === 'BNB') {
        // Check if BNB swaps are enabled
        const bnbSwapsStatus = await swapContract.bnbSwapsEnabled();
        if (!bnbSwapsStatus) {
          throw new Error("BNB swaps are currently disabled");
        }
        
        // Convert BNB price to wei format (1e18 precision)
        const bnbPriceInWei = ethers.utils.parseUnits(bnbPrice.toString(), 18);
        
        tx = await swapContract.swapTokenToBNB(amountInWei, bnbPriceInWei);
      } else if (fromToken === 'GRICH' && toToken === 'USDT') {
        tx = await swapContract.swapTokenToUSDT(amountInWei);
      }
      
      // Wait for transaction to complete
      const receipt = await tx.wait();
      setTxHash(receipt.transactionHash);
      
      // Reset inputs
      setFromAmount('');
      setToAmount('');
      
      // Refresh balances
      await refreshBalances();
    } catch (error) {
      console.error("Swap error:", error);
      setError(error.message || "Swap failed. Please try again.");
    } finally {
      setIsSwapping(false);
    }
  };
  
  // Switch tokens
  const handleSwitchTokens = () => {
    setFromToken(toToken);
    setToToken(fromToken);
    setFromAmount(toAmount);
    setToAmount(fromAmount);
  };

  // Handle tab switch
  const handleTabSwitch = (tab) => {
    setActiveTab(tab);
    if (tab === 'USDT') {
      setFromToken('USDT');
      setToToken('GRICH');
    } else {
      setFromToken('BNB');
      setToToken('GRICH');
    }
    setFromAmount('');
    setToAmount('');
  };
  
  // Get token icon
  const getTokenIcon = (token) => {
    switch(token) {
      case 'USDT':
        return <img src={usdtImg} alt="USDT" className="token-icon" />;
      case 'BNB':
        return <img src={bnbImg} alt="BNB" className="token-icon" />;
      case 'GRICH':
        return <img src={grichImg} alt="GRICH" className="token-icon" />;
      default:
        return null;
    }
  };

  // Calculate price impact
  const calculatePriceImpact = () => {
    if (!fromAmount || parseFloat(fromAmount) <= 0) return '0.00';
    // This is a simplified calculation, in production you would compare to actual liquidity
    return '< 0.01';
  };

  // Calculate minimum received
  const calculateMinReceived = () => {
    if (!toAmount || parseFloat(toAmount) <= 0) return '0';
    const minAmount = parseFloat(toAmount) * (1 - slippage / 100);
    return minAmount.toFixed(6);
  };

  // Check if swap is disabled
  const isSwapDisabled = () => {
    if (!account) return true;
    if (!fromAmount || parseFloat(fromAmount) <= 0) return true;
    if (isSwapping) return true;
    
    // Check if BNB swaps are disabled when using BNB
    if (!bnbSwapsEnabled && (fromToken === 'BNB' || toToken === 'BNB')) {
      return true;
    }
    
    return false;
  };
  
  // Render the main component
  return (
    <div className="grich-swap-container">
      {/* Header */}
      <header className="swap-header">
        <div className="logo-area">
          <h1><Zap className="logo-icon" /> GRICH SWAP</h1>
          <div className="price-display">
            <Activity className="price-icon" />
            <div className="price-info">
              <span className="price-value">${grichPrice.toFixed(2)}</span>
              <span className="price-label">GRICH Coin Price</span>
            </div>
          </div>
        </div>
        <div className="wallet-connect">
          <WalletConnector
            onConnect={handleConnect}
            onDisconnect={handleDisconnect}
            onError={handleError}
            buttonClassName="connect-button"
          />
        </div>
      </header>
      
      {/* Main Swap Interface */}
      <div className="swap-card">
        <div className="swap-card-header">
          <h2>Swap Tokens</h2>
          <div className="swap-settings">
            <button 
              className="icon-button" 
              onClick={() => setIsSettingsOpen(!isSettingsOpen)}
            >
              <Settings size={20} />
            </button>
            <button className="icon-button" onClick={refreshBalances}>
              <RefreshCw size={20} />
            </button>
          </div>
        </div>
        
        {/* Tab Selector */}
        <div className="token-tabs">
          <button 
            className={`tab-button ${activeTab === 'USDT' ? 'active' : ''}`}
            onClick={() => handleTabSwitch('USDT')}
          >
            USDT
          </button>
          <button 
            className={`tab-button ${activeTab === 'BNB' ? 'active' : ''} ${!bnbSwapsEnabled ? 'disabled' : ''}`}
            onClick={() => bnbSwapsEnabled && handleTabSwitch('BNB')}
            disabled={!bnbSwapsEnabled}
          >
            BNB
            {!bnbSwapsEnabled && <span className="disabled-indicator">Not Available</span>}
          </button>
        </div>
        
        {/* BNB Swap Disabled Warning */}
        {!bnbSwapsEnabled && activeTab === 'BNB' && (
          <div className="warning-message">
            <AlertTriangle className="warning-icon" />
            <span>BNB swaps are currently disabled by the contract owner.</span>
          </div>
        )}
        
        {/* Settings Panel */}
        {isSettingsOpen && (
          <div className="settings-panel">
            <div className="slippage-setting">
              <label>Slippage Tolerance:</label>
              <div className="slippage-buttons">
                <button 
                  className={slippage === 0.5 ? 'active' : ''} 
                  onClick={() => setSlippage(0.5)}
                >
                  0.5%
                </button>
                <button 
                  className={slippage === 1 ? 'active' : ''} 
                  onClick={() => setSlippage(1)}
                >
                  1%
                </button>
                <button 
                  className={slippage === 2 ? 'active' : ''} 
                  onClick={() => setSlippage(2)}
                >
                  2%
                </button>
                <div className="custom-slippage">
                  <input 
                    type="number" 
                    value={slippage} 
                    onChange={(e) => setSlippage(parseFloat(e.target.value) || 0)} 
                    min="0.1" 
                    max="50"
                    step="0.1"
                  />
                  <span>%</span>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* From Token Section */}
        <div className="token-input-container">
          <div className="token-input-header">
            <span>Amount</span>
            {account && (
              <span className="balance-display">
                Balance: {' '}
                {fromToken === 'BNB' 
                  ? parseFloat(bnbBalance).toFixed(4)
                  : fromToken === 'USDT' 
                    ? parseFloat(usdtBalance).toFixed(2)
                    : parseFloat(tokenBalance).toFixed(2)}
              </span>
            )}
          </div>
          <div className="token-input-field">
            <input 
              type="text"
              value={fromAmount}
              onChange={(e) => handleFromAmountChange(e.target.value)}
              placeholder="0"
              className="token-amount-input"
            />
            <div className="token-selector">
              {getTokenIcon(fromToken)} {fromToken}
            </div>
          </div>
        </div>
        
        {/* Switch Button */}
        <div className="switch-tokens-button-container">
          <button 
            className="switch-tokens-button" 
            onClick={handleSwitchTokens}
            disabled={!bnbSwapsEnabled && (fromToken === 'BNB' || toToken === 'BNB')}
          >
            <ArrowDown size={20} />
          </button>
        </div>
        
        {/* To Token Section */}
        <div className="token-input-container">
          <div className="token-input-header">
            <span>Amount</span>
            {account && (
              <span className="balance-display">
                Balance: {' '}
                {toToken === 'BNB' 
                  ? parseFloat(bnbBalance).toFixed(4)
                  : toToken === 'USDT' 
                    ? parseFloat(usdtBalance).toFixed(2)
                    : parseFloat(tokenBalance).toFixed(2)}
              </span>
            )}
          </div>
          <div className="token-input-field">
            <input 
              type="text"
              value={toAmount}
              readOnly
              placeholder="0"
              className="token-amount-input"
            />
            <div className="token-selector">
              {getTokenIcon(toToken)} {toToken}
            </div>
          </div>
        </div>
        
        {/* Swap Info */}
        <div className="swap-info-container">
          <div className="swap-info-row">
            <span>Liquidity Provider Fee</span>
            <span>1%</span>
          </div>
          <div className="swap-info-row">
            <span>Minimum Received</span>
            <span>{calculateMinReceived()} {toToken}</span>
          </div>
          <div className="swap-info-row">
            <span>Price Impact</span>
            <span className="price-impact-value">{'< 0.01%'}</span>
          </div>
          <div className="swap-info-row">
            <span>Swap Path</span>
            <span className="swap-path">{fromToken} → {toToken}</span>
          </div>
        </div>
        
        {/* Swap Button */}
        <button 
          className="swap-button" 
          onClick={handleSwap}
          disabled={isSwapDisabled()}
        >
          {!account 
            ? 'Connect Wallet' 
            : isSwapping 
              ? 'Swapping...' 
              : !bnbSwapsEnabled && (fromToken === 'BNB' || toToken === 'BNB')
                ? 'BNB Swaps Unavailable'
                : 'Swap Tokens'}
        </button>
        
        {/* Error Display */}
        {error && <div className="error-message">{error}</div>}
        
        {/* Transaction Hash Display */}
        {txHash && (
          <div className="transaction-info">
            <p>Transaction successful!</p>
            <a 
              href={`https://opbnbscan.com/tx/${txHash}`} 
              target="_blank" 
              rel="noopener noreferrer"
            >
              View on Explorer
            </a>
          </div>
        )}
      </div>
    </div>
  );
};

export default GrichSwap;